/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IComponentStyles } from '@fluentui/foundation-legacy';
import { DefaultButton, IconButton, PrimaryButton } from '@fluentui/react/lib/Button';
import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { ILabelStyleProps, ILabelStyles, Label } from '@fluentui/react/lib/Label';
import { MessageBar, MessageBarType } from '@fluentui/react/lib/MessageBar';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import { IStackProps, IStackSlots, Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { CalloutButton } from 'src/core/components/CalloutButton';
import { LuisDropdown } from 'src/core/components/LuisDropdown';
import { LuisText } from 'src/core/components/LuisText';
import { LuisTooltip } from 'src/core/components/LuisTooltip';
import { TruncatedText } from 'src/core/components/TruncatedText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { logButtonClick } from 'src/core/services/telemetry/eventsNamesTemplate';
import { AzureCsAccountStore } from 'src/core/stores/AzureCsAccountStore';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';
import { AOAIResourceConnector } from 'src/modules/gptPrediction/components/AOAIResourceConnector';
import { isGptFeatureDisabled, shouldAuthTypeDisableGpt, shouldRegionDisableGpt } from 'src/modules/gptPrediction/utils/gptUtils';

type UtterancesGenerationWithGptPaneProps = {
    onCloseClicked: () => void;
};

const messages = defineMessages({
    cancel: { id: 'clu.utterancesGenerationWithGptPane.cancel', defaultMessage: 'Cancel' },
    title: { id: 'clu.utterancesGenerationWithGptPane.title', defaultMessage: 'Suggest utterances with GPT' },
    pickIntentTitle: { id: 'clu.utterancesGenerationWithGptPane.pickIntentTitle', defaultMessage: 'Pick your intent' },
    intentsLabel: { id: 'clu.utterancesGenerationWithGptPane.intentsLabel', defaultMessage: 'Select intent for suggestions' },
    generateUtterances: { id: 'clu.utterancesGenerationWithGptPane.generateUtterances', defaultMessage: 'Generate utterances' },
    languageAccountLabel: { id: 'clu.utterancesGenerationWithGptPane.languageAccountLabel', defaultMessage: 'Language resource:' },
    enableManagedIdentity: { id: 'clu.utterancesGenerationWithGptPane.enableManagedIdentity', defaultMessage: 'Enable managed identity' },
    enableManagedIdentityDescription: {
        id: 'clu.utterancesGenerationWithGptPane.enableManagedIdentityDescription',
        defaultMessage: 'Enable your Language resource’s managed identity to use this feature.'
    },
    enableManagedIdentityInfo: {
        id: 'clu.utterancesGenerationWithGptPane.enableManagedIdentityInfo',
        defaultMessage:
            'A system assigned managed identity enables Azure resources to authenticate to cloud services (e.g. Azure Key Vault) without storing credentials in code.'
    },
    noUtterancesFound: {
        id: 'clu.utterancesGenerationWithGptPane.noUtterancesFound',
        defaultMessage: 'Please add at least 5 utterances to the selected intent to be able to suggest utterances for you.'
    },
    regionNotSupported: {
        id: 'clu.utterancesGenerationWithGptPane.regionNotSupported',
        defaultMessage:
            'This feature is not supported in your language resource region, the supported regions are "eastus", "southcentralus" or "westeurope".'
    },
    authTypeNotSupported: {
        id: 'clu.utterancesGenerationWithGptPane.authTypeNotSupported',
        defaultMessage:
            'Your language resource authentication type is not supported in this feature, this feature is allowed only with key based authentication.'
    },
    pickIntentDescription: {
        id: 'clu.utterancesGenerationWithGptPane.pickIntentDescription',
        defaultMessage: 'Select your intent and the system will use Azure OpenAI to generate a few utterances for that intent'
    },
    azureOpenAiResourceTitle: {
        id: 'clu.utterancesGenerationWithGptPane.azureOpenAiResourceTitle',
        defaultMessage: 'Select Azure OpenAI resource'
    },
    azureOpenAiResourceDescription: {
        id: 'clu.utterancesGenerationWithGptPane.azureOpenAiResourceDescription',
        defaultMessage:
            'In order to use GPT to suggest utterances, you must select an Azure OpenAI resource and a current deployment from that resource.'
    },
    description: {
        id: 'clu.utterancesGenerationWithGptPane.description',
        defaultMessage:
            'You can use GPT to suggest utterances for an intent. After the utterances are generated, you can review or edit the suggestions'
    },
    closeFiltersPaneButtonAriaLabel: {
        id: 'clu.utterancesGenerationWithGptPane.closeFiltersPaneButtonAriaLabel',
        defaultMessage: 'Close suggest pane'
    }
});

const scopedTheme = getScopedTheme('TagUtterancesPageSidePaneContainer');
const containerStyles = classNamesFunction<IStackProps, IComponentStyles<IStackSlots>>()({ root: { padding: 12, overflowY: 'auto' } });
const labelStyles = classNamesFunction<ILabelStyleProps, ILabelStyles>()({
    root: { display: 'flex', fontWeight: 400, selectors: { '::after': { paddingRight: 0 } } }
});

export const UtterancesGenerationWithGptPane = observer((props: UtterancesGenerationWithGptPaneProps) => {
    const { onCloseClicked } = props;
    const { localize } = useLocalization();
    const { serviceName } = getPortalFromUrl();
    const generationTracker = useProgressTracker();
    const { dispatcher, rootStore } = useGlobalState();
    const managedIdentityTracker = useProgressTracker();

    const currentLanguageResource = rootStore.azureStore.session.csAccountStore;

    const [selectedIntentId, setSelectedIntentId] = React.useState<string>();
    const [isManagedIdentityEnabled] = React.useState(currentLanguageResource.isIdentityEnabled);
    const [selectedAzureOpenAiDeploymentId, setSelectedAzureOpenAiDeploymentId] = React.useState<string>();
    const [selectedAzureOpenAiResource, setSelectedAzureOpenAiResource] = React.useState<AzureCsAccountStore>();

    const { intentStoreHouse, gptJobsStoreHouse, jsonData, id: currentProjectId } = rootStore.currentCluProjectStore;
    const selectedIntentUtterances = jsonData.utterances?.filter(u => u.intent === selectedIntentId) ?? [];
    const areNoUtterancesFound = selectedIntentUtterances.length === 0;
    const gptJobsInProgress = gptJobsStoreHouse.items
        .filter(j => j.status === 'notStarted' || j.status === 'running')
        .sort((j1, j2) => new Date(j2.createdDateTime).getMilliseconds() - new Date(j1.createdDateTime).getMilliseconds());
    const isGenerationLoading = gptJobsInProgress.length !== 0 || generationTracker.isRunning;
    const areRegionAndAuthTypeNotSupported = isGptFeatureDisabled(
        rootStore.serviceKey.keyType,
        rootStore.azureStore.session.csAccountStore.region
    );
    const isGenerationDisabled =
        areRegionAndAuthTypeNotSupported ||
        !selectedAzureOpenAiDeploymentId ||
        !selectedIntentId ||
        isGenerationLoading ||
        areNoUtterancesFound;

    const getTooltipMessage = () => {
        if (shouldAuthTypeDisableGpt(rootStore.serviceKey.keyType)) {
            return localize(messages.authTypeNotSupported);
        }
        if (areRegionAndAuthTypeNotSupported) {
            return localize(messages.regionNotSupported);
        }
        if (areNoUtterancesFound) {
            return localize(messages.noUtterancesFound);
        }
    };

    const generateUtterances = () => {
        logButtonClick(serviceName, 'DataLabeling', `GenerateUtterances`, currentProjectId);
        dispatcher.dispatch('generateUtterancesWithGpt', {
            toastData: {},
            tracker: generationTracker,
            utterances: selectedIntentUtterances.slice(0, 5),
            azureOpenAiDeploymentName: selectedAzureOpenAiDeploymentId,
            azureOpenAiResourceRegion: selectedAzureOpenAiResource.region,
            intents: intentStoreHouse.items.filter(intent => intent.id === selectedIntentId).map(intent => intent.name)
        });
    };

    const handleClosePane = () => {
        logButtonClick(serviceName, 'DataLabeling', `CloseSuggestUtterances`, currentProjectId);
        onCloseClicked();
    };

    return (
        <Stack verticalFill verticalAlign="space-between" tokens={{ childrenGap: 5 }}>
            <Stack verticalFill>
                <Stack horizontal horizontalAlign="space-between" verticalAlign="center" styles={containerStyles}>
                    <LuisText variant="xLarge">{localize(messages.title)}</LuisText>
                    <IconButton
                        autoFocus
                        onClick={handleClosePane}
                        iconProps={{ iconName: 'ChromeClose' }}
                        ariaLabel={localize(messages.closeFiltersPaneButtonAriaLabel)}
                        styles={{ icon: { color: scopedTheme.iconColor, fontSize: '12px' } }}
                    />
                </Stack>

                {shouldRegionDisableGpt(currentLanguageResource.region) && (
                    <MessageBar messageBarType={MessageBarType.severeWarning}>
                        <LuisText>{localize(messages.regionNotSupported)}</LuisText>
                    </MessageBar>
                )}

                <Stack tokens={{ childrenGap: 17 }} styles={containerStyles}>
                    <LuisText>{localize(messages.description)}</LuisText>

                    {!isManagedIdentityEnabled && [
                        <Stack horizontal verticalAlign="center">
                            <LuisText variant="mediumPlus" fontWeight={600} styles={{ root: { textTransform: 'capitalize' } }}>
                                {localize(messages.enableManagedIdentity)}
                            </LuisText>
                            <CalloutButton content={localize(messages.enableManagedIdentityInfo)} />
                        </Stack>,
                        <LuisText>{localize(messages.enableManagedIdentityDescription)}</LuisText>,
                        <Stack tokens={{ childrenGap: 8 }}>
                            <TruncatedText title={currentLanguageResource.name}>
                                <LuisText fontWeight={600}>{localize(messages.languageAccountLabel)}</LuisText>&nbsp;
                                {currentLanguageResource.name}
                            </TruncatedText>
                            <Checkbox
                                label={localize(messages.enableManagedIdentity)}
                                onRenderLabel={(labelProps, labelDefaultRenderer) => (
                                    <Stack horizontal tokens={{ childrenGap: 5 }}>
                                        {managedIdentityTracker.isRunning && <Spinner size={SpinnerSize.small} />}
                                        {labelDefaultRenderer(labelProps)}
                                    </Stack>
                                )}
                                checked={currentLanguageResource.isIdentityEnabled}
                                onChange={(_, isChecked) =>
                                    dispatcher.dispatch('updateAzureCsAccountByPath', {
                                        toastData: {},
                                        isIdentityEnabled: isChecked,
                                        tracker: managedIdentityTracker,
                                        localStore: currentLanguageResource,
                                        skuCode: currentLanguageResource.sku.code
                                    })
                                }
                            />
                        </Stack>
                    ]}

                    <LuisText variant="mediumPlus" fontWeight={600}>
                        {localize(messages.azureOpenAiResourceTitle)}
                    </LuisText>

                    <LuisText>{localize(messages.azureOpenAiResourceDescription)}</LuisText>

                    <AOAIResourceConnector
                        selectedAzureOpenAiResource={selectedAzureOpenAiResource}
                        onAzureOpenAiResourceChange={setSelectedAzureOpenAiResource}
                        selectedAzureOpenAiDeploymentId={selectedAzureOpenAiDeploymentId}
                        onAzureOpenAiDeploymentIdChange={setSelectedAzureOpenAiDeploymentId}
                    />

                    <LuisText variant="mediumPlus" fontWeight={600}>
                        {localize(messages.pickIntentTitle)}
                    </LuisText>

                    <LuisText>{localize(messages.pickIntentDescription)}</LuisText>

                    <LuisDropdown
                        selectedKey={selectedIntentId}
                        placeholder={localize(messages.intentsLabel)}
                        onChange={(_, o) => setSelectedIntentId(o.key as string)}
                        options={intentStoreHouse.items.filter(intent => !intent.orchestrationType).map(i => ({ key: i.id, text: i.name }))}
                        onRenderLabel={() => (
                            <Stack horizontal verticalAlign="center">
                                <Label styles={labelStyles} required>
                                    {localize(messages.intentsLabel)}
                                </Label>
                                <CalloutButton content={localize(messages.intentsLabel)} />
                            </Stack>
                        )}
                    />
                </Stack>
            </Stack>

            <Stack horizontal tokens={{ childrenGap: 8 }} styles={containerStyles}>
                <LuisTooltip content={getTooltipMessage()}>
                    <PrimaryButton onClick={generateUtterances} disabled={isGenerationDisabled}>
                        <Stack horizontal tokens={{ childrenGap: 5 }}>
                            {isGenerationLoading && <Spinner size={SpinnerSize.xSmall} />}
                            <LuisText>{localize(messages.generateUtterances)}</LuisText>
                        </Stack>
                    </PrimaryButton>
                </LuisTooltip>
                <DefaultButton onClick={handleClosePane}>{localize(messages.cancel)}</DefaultButton>
            </Stack>
        </Stack>
    );
});
